// Pagina singolo articolo help con Markdown e feedback
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { ThumbsUp, ThumbsDown, ArrowLeft, Clock } from 'lucide-react'
import { useState } from 'react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Skeleton } from '../../components/ui/Skeleton'
import { Alert } from '../../components/ui/Alert'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { formatDate } from '../../lib/utils'
import api from '../../lib/api'

interface HelpArticle {
  id: string
  slug: string
  title: string
  content: string
  category: string
  categoryName: string
  updatedAt: string
  readingTimeMinutes: number
  relatedArticles: Array<{ id: string; slug: string; title: string }>
}

// Render Markdown semplice (senza dipendenze aggiuntive)
function SimpleMarkdown({ content }: { content: string }) {
  const html = content
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-4 mb-2 text-[var(--fc-foreground)]">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mt-6 mb-3 text-[var(--fc-foreground)]">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mt-6 mb-4 text-[var(--fc-foreground)]">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="font-mono bg-[var(--fc-surface-overlay)] px-1 rounded text-sm">$1</code>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
    .replace(/\n\n/g, '</p><p class="mb-3">')

  return (
    <div
      className="prose prose-sm max-w-none text-[var(--fc-foreground)]"
      dangerouslySetInnerHTML={{ __html: `<p class="mb-3">${html}</p>` }}
    />
  )
}

export function HelpArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const [feedbackSent, setFeedbackSent] = useState(false)

  const { data: article, isLoading, error } = useQuery({
    queryKey: ['help', 'article', slug],
    queryFn: async () => {
      const res = await api.get<HelpArticle>(`/help/articles/${slug}`)
      return res.data
    },
    enabled: Boolean(slug),
  })

  const feedbackMutation = useMutation({
    mutationFn: async (helpful: boolean) => {
      await api.post(`/help/articles/${article?.id}/feedback`, { helpful })
    },
    onSuccess: () => setFeedbackSent(true),
  })

  if (error) return <Alert variant="error">Articolo non trovato.</Alert>

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link
        to="/help"
        className="flex items-center gap-1 text-sm text-[var(--fc-primary)] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        Centro assistenza
      </Link>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton height={32} className="w-3/4" />
          <Skeleton height={16} className="w-48" />
          <Skeleton height={200} className="rounded-lg" />
        </div>
      ) : !article ? null : (
        <>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="primary">{article.categoryName}</Badge>
              <span className="flex items-center gap-1 text-xs text-[var(--fc-foreground-muted)]">
                <Clock className="h-3 w-3" aria-hidden="true" />
                {article.readingTimeMinutes} min lettura
              </span>
              <span className="text-xs text-[var(--fc-foreground-subtle)]">
                Aggiornato {formatDate(article.updatedAt, 'dd/MM/yyyy')}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-[var(--fc-foreground)]">{article.title}</h1>
          </div>

          {/* Contenuto articolo */}
          <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-6">
            <SimpleMarkdown content={article.content} />
          </div>

          {/* Feedback */}
          <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface-raised)] p-4">
            {feedbackSent ? (
              <Alert variant="success">Grazie per il feedback!</Alert>
            ) : (
              <div className="flex items-center gap-4">
                <p className="text-sm text-[var(--fc-foreground)]">Questo articolo è stato utile?</p>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<ThumbsUp className="h-4 w-4" />}
                  onClick={() => feedbackMutation.mutate(true)}
                >
                  Sì
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<ThumbsDown className="h-4 w-4" />}
                  onClick={() => feedbackMutation.mutate(false)}
                >
                  No
                </Button>
              </div>
            )}
          </div>

          {/* Articoli correlati */}
          {article.relatedArticles.length > 0 && (
            <div>
              <h2 className="mb-3 text-base font-semibold text-[var(--fc-foreground)]">Articoli correlati</h2>
              <div className="divide-y divide-[var(--fc-border)] rounded-lg border border-[var(--fc-border)]">
                {article.relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/help/article/${rel.slug}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--fc-surface-raised)] transition-colors text-sm text-[var(--fc-foreground)] hover:text-[var(--fc-primary)]"
                  >
                    {rel.title}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
